/**
 * All meetings (screenshots/117–118): pure helpers for the list (grouped by Chicago day) and the month grid.
 * Dates are computed in America/Chicago so a 10 PM UTC meeting never lands on the wrong day.
 */
export interface MeetingLite { id: string; body: string; kind: "council" | "committee"; startsAt: string; location: string; items: number | null; hasVote: boolean; status: "upcoming" | "live" | "past" }
export const TZ = "America/Chicago";

/** "2026-10-08" for an instant, in Chicago. */
export const dayKey = (iso: string | Date) => new Intl.DateTimeFormat("en-CA", { timeZone: TZ, year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date(iso));
export const dayLabel = (key: string) => new Date(`${key}T12:00:00Z`).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", timeZone: "UTC" });
export const timeLabel = (iso: string) => new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZone: TZ });

export interface MeetingFilters { when: "upcoming" | "past" | "all"; bodies?: string[]; hasVote?: boolean; q?: string }

export function filterMeetings(ms: MeetingLite[], f: MeetingFilters, now: Date): MeetingLite[] {
  const today = dayKey(now), q = f.q?.trim().toLowerCase();
  return ms.filter((m) => {
    const k = dayKey(m.startsAt);
    if (f.when === "upcoming" && k < today && m.status !== "live") return false;
    if (f.when === "past" && (k >= today || m.status === "live")) return false;
    if (f.bodies?.length && !f.bodies.includes(m.body)) return false;
    if (f.hasVote && !m.hasVote) return false;
    if (q && !m.body.toLowerCase().includes(q) && !m.location.toLowerCase().includes(q)) return false;
    return true;
  }).sort((a, b) => (f.when === "past" ? b.startsAt.localeCompare(a.startsAt) : a.startsAt.localeCompare(b.startsAt)));
}

/** List view: one group per Chicago day, "Today" marked. */
export function groupByDay(ms: MeetingLite[], now: Date): { key: string; label: string; today: boolean; meetings: MeetingLite[] }[] {
  const today = dayKey(now), out = new Map<string, MeetingLite[]>();
  for (const m of ms) { const k = dayKey(m.startsAt); out.set(k, [...(out.get(k) ?? []), m]); }
  return [...out].map(([key, meetings]) => ({ key, label: dayLabel(key), today: key === today, meetings }));
}

/** Month view: whole weeks (Sun–Sat) covering the month; days outside the month are marked. */
export function monthGrid(year: number, month: number /* 1–12 */): { key: string; day: number; inMonth: boolean }[][] {
  const first = new Date(Date.UTC(year, month - 1, 1)), start = new Date(first); start.setUTCDate(1 - first.getUTCDay());
  const last = new Date(Date.UTC(year, month, 0)), weeks: { key: string; day: number; inMonth: boolean }[][] = [];
  for (let d = new Date(start); d <= last || d.getUTCDay() !== 0; ) {
    const week = [];
    for (let i = 0; i < 7; i++) { week.push({ key: d.toISOString().slice(0, 10), day: d.getUTCDate(), inMonth: d.getUTCMonth() === month - 1 }); d.setUTCDate(d.getUTCDate() + 1); }
    weeks.push(week);
  }
  return weeks;
}

/** Following a body adds all its meetings to Following. */
export const toggleBody = (followed: string[], body: string) => (followed.includes(body) ? followed.filter((b) => b !== body) : [...followed, body]);
